import { memo, useState } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Activity, HardDrive, Wifi, DatabaseZap, Disc, Zap } from 'lucide-react';
import DBTypeIcon from '../../components/DBTypeIcon';

export interface MetricTileItem {
    key?: string;
    label?: string;
    suffix?: string;
    digits?: number;
    value?: any;
}

export interface DBNodeData {
    nodeId?: string;
    dbType?: string;
    role?: string;
    displayRole?: string;
    display_role?: string;
    isElected?: boolean;
    upstreamLabel?: string;
    state?: string;
    host?: string;
    hostname?: string;
    port?: number | string;
    tps?: number;
    qps?: number;
    connectionsUtilPct?: number;
    connectionsTotal?: number;
    connectionsMax?: number;
    cacheHitPct?: number;
    indexHitPct?: number;
    dbSizeMb?: number;
    databaseCount?: number;
    lagSec?: number;
    slowQueries?: number;
    isPrimary?: boolean;
    isSelected?: boolean;
    metricTiles?: MetricTileItem[];
    metric_tiles?: MetricTileItem[];
}

// DB size display helper
export const formatDbSize = (mb?: number) => {
    if (mb == null) return null;
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    if (mb > 0 && mb < 0.01) return `<0.01 MB`;
    if (mb >= 0.01 && mb < 1) return `${mb.toFixed(2)} MB`;
    return `${mb.toFixed(0)} MB`;
};

// Formatter for dynamic metric tile values
export const formatTileValue = (tile: MetricTileItem) => {
    if (tile.value == null || tile.value === '') return '—';
    if (typeof tile.value !== 'number' || isNaN(tile.value)) {
        return `${tile.value}${tile.suffix || ''}`;
    }

    const val = tile.value;
    const rawSuffix = tile.suffix || '';
    const cleanSuffix = rawSuffix.trim();

    // Special handling for DB / Memory size in MB
    if (tile.key?.includes('size_mb') || cleanSuffix.toLowerCase() === 'mb') {
        if (val >= 1024) return `${(val / 1024).toFixed(1)} GB`;
        if (val > 0 && val < 0.01) return `<0.01 MB`;
        if (val >= 0.01 && val < 1) return `${val.toFixed(2)} MB`;
        const d = tile.digits ?? 0;
        return `${d > 0 ? val.toFixed(d) : Math.round(val)} MB`;
    }

    const digits = tile.digits != null ? tile.digits : (Number.isInteger(val) ? 0 : 1);
    const formattedNum = Number(val.toFixed(digits)).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
    });

    const suffixToUse = rawSuffix.startsWith(' ') || rawSuffix.startsWith('%') ? rawSuffix : (rawSuffix ? ` ${rawSuffix}` : '');
    return `${formattedNum}${suffixToUse}`;
};

// Icon & palette matcher for dynamic metric tiles
export const getTileStyle = (key?: string, label?: string, index: number = 0, isOffline: boolean = false) => {
    if (isOffline) {
        return {
            containerClass: 'bg-red-50 dark:bg-[#220c10] border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300',
            iconClass: 'text-red-600 dark:text-red-400',
            icon: Activity,
        };
    }

    const lowerKey = (key || '').toLowerCase();
    const lowerLabel = (label || '').toLowerCase();

    // TPS / QPS / Throughput
    if (lowerKey.includes('tps') || lowerKey.includes('qps') || lowerLabel.includes('tps') || lowerLabel.includes('qps')) {
        return {
            containerClass: 'bg-blue-50 dark:bg-[#0d1c38] border-blue-200 dark:border-[#1d3866] text-blue-700 dark:text-blue-300',
            iconClass: 'text-blue-600 dark:text-blue-400',
            icon: Activity,
        };
    }

    // Disk / Storage
    if (lowerKey.includes('disk') || lowerLabel.includes('disk')) {
        return {
            containerClass: 'bg-amber-50 dark:bg-[#261609] border-amber-200 dark:border-[#543011] text-amber-700 dark:text-amber-300',
            iconClass: 'text-amber-600 dark:text-amber-400',
            icon: Disc,
        };
    }

    // DB Size / Memory
    if (lowerKey.includes('size') || lowerLabel.includes('size') || lowerKey.includes('memory')) {
        return {
            containerClass: 'bg-purple-50 dark:bg-[#1c1333] border-purple-200 dark:border-[#3b2366] text-purple-700 dark:text-purple-300',
            iconClass: 'text-purple-600 dark:text-purple-400',
            icon: HardDrive,
        };
    }

    // Connections
    if (lowerKey.includes('conn') || lowerLabel.includes('conn')) {
        return {
            containerClass: 'bg-cyan-50 dark:bg-[#0a2334] border-cyan-200 dark:border-[#154666] text-cyan-700 dark:text-cyan-300',
            iconClass: 'text-cyan-600 dark:text-cyan-400',
            icon: Wifi,
        };
    }

    // Cache / Buffer
    if (lowerKey.includes('cache') || lowerLabel.includes('cache') || lowerKey.includes('buffer')) {
        return {
            containerClass: 'bg-emerald-50 dark:bg-[#092823] border-emerald-200 dark:border-[#135246] text-emerald-700 dark:text-emerald-300',
            iconClass: 'text-emerald-600 dark:text-emerald-400',
            icon: DatabaseZap,
        };
    }

    // Active Queries / Queries
    if (lowerKey.includes('quer') || lowerLabel.includes('quer')) {
        return {
            containerClass: 'bg-teal-50 dark:bg-[#0a2628] border-teal-200 dark:border-[#134e4a] text-teal-700 dark:text-teal-300',
            iconClass: 'text-teal-600 dark:text-teal-400',
            icon: Zap,
        };
    }

    // Fallback palette by index
    const fallbacks = [
        { containerClass: 'bg-blue-50 dark:bg-[#0d1c38] border-blue-200 dark:border-[#1d3866] text-blue-700 dark:text-blue-300', iconClass: 'text-blue-600 dark:text-blue-400', icon: Activity },
        { containerClass: 'bg-purple-50 dark:bg-[#1c1333] border-purple-200 dark:border-[#3b2366] text-purple-700 dark:text-purple-300', iconClass: 'text-purple-600 dark:text-purple-400', icon: HardDrive },
        { containerClass: 'bg-cyan-50 dark:bg-[#0a2334] border-cyan-200 dark:border-[#154666] text-cyan-700 dark:text-cyan-300', iconClass: 'text-cyan-600 dark:text-cyan-400', icon: Wifi },
        { containerClass: 'bg-emerald-50 dark:bg-[#092823] border-emerald-200 dark:border-[#135246] text-emerald-700 dark:text-emerald-300', iconClass: 'text-emerald-600 dark:text-emerald-400', icon: DatabaseZap },
    ];

    return fallbacks[index % fallbacks.length];
};

const DBNode = memo(({ id, data }: NodeProps<DBNodeData>) => {
    const {
        dbType,
        role,
        displayRole,
        display_role,
        hostname,
        state,
        host,
        port,
        tps,
        qps,
        connectionsUtilPct,
        cacheHitPct,
        dbSizeMb,
        isPrimary,
        isSelected,
        metricTiles,
        metric_tiles,
    } = data;

    const [isHovered, setIsHovered] = useState(false);

    const isOnline = state?.toLowerCase() === 'online' || state?.toLowerCase() === 'healthy';
    const isDegraded = state?.toLowerCase() === 'degraded';
    const isOffline = !isOnline && !isDegraded;

    const activeDisplayRole = displayRole || display_role || role || 'NODE';
    const isWasPrimary = activeDisplayRole.toLowerCase() === 'was primary';

    const isPrimaryRole = !isWasPrimary && (isPrimary || role?.toLowerCase() === 'primary' || role?.toLowerCase() === 'standalone');

    const handleStyle = `!w-2.5 !h-2.5 !bg-slate-400 dark:!bg-slate-300 !border-2 !border-white dark:!border-[#070b16] !rounded-full transition-all duration-200 hover:scale-125 hover:!bg-cyan-500 dark:hover:!bg-cyan-400 hover:!border-cyan-200`;

    // Border & Glow styling
    const borderStyle = isOffline
        ? 'border-red-500/80 bg-red-50/90 dark:bg-[#16080a] shadow-[0_0_20px_rgba(239,68,68,0.2)] dark:shadow-[0_0_20px_rgba(239,68,68,0.35)]'
        : isSelected
            ? isPrimaryRole
                ? 'border-amber-500 dark:border-amber-400 ring-2 ring-amber-400/60 ring-offset-2 ring-offset-white dark:ring-offset-[#090e1a] shadow-[0_0_25px_rgba(245,158,11,0.3)] dark:shadow-[0_0_25px_rgba(245,158,11,0.45)]'
                : 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-400/60 ring-offset-2 ring-offset-white dark:ring-offset-[#090e1a] shadow-[0_0_25px_rgba(59,130,246,0.3)] dark:shadow-[0_0_25px_rgba(59,130,246,0.45)]'
            : isHovered
                ? isPrimaryRole
                    ? 'border-amber-500 dark:border-amber-400 scale-[1.02] shadow-[0_0_20px_rgba(245,158,11,0.2)] dark:shadow-[0_0_20px_rgba(245,158,11,0.3)]'
                    : 'border-blue-500 dark:border-blue-400 scale-[1.02] shadow-[0_0_20px_rgba(59,130,246,0.2)] dark:shadow-[0_0_20px_rgba(59,130,246,0.3)]'
                : isPrimaryRole
                    ? 'border-amber-500/60 shadow-[0_0_12px_rgba(245,158,11,0.1)] dark:shadow-[0_0_12px_rgba(245,158,11,0.15)] bg-white dark:bg-[#090e1a]'
                    : 'border-blue-500/60 shadow-[0_0_12px_rgba(59,130,246,0.1)] dark:shadow-[0_0_12px_rgba(59,130,246,0.15)] bg-white dark:bg-[#090e1a]';

    // Header gradient
    const headerBg = isOffline
        ? 'bg-gradient-to-b from-red-100 to-red-50 dark:from-[#360e13] dark:to-[#1d070a] border-red-300 dark:border-red-500/50'
        : isPrimaryRole
            ? 'bg-gradient-to-b from-amber-100/90 to-amber-50/80 dark:from-[#2e1d0d] dark:to-[#1c1208] border-amber-300 dark:border-amber-500/40'
            : 'bg-gradient-to-b from-blue-100/90 to-slate-50 dark:from-[#0e213e] dark:to-[#081324] border-cyan-300 dark:border-cyan-500/40';

    const displayTps = tps ?? qps;
    const tiles = (metricTiles && metricTiles.length > 0) ? metricTiles : metric_tiles;

    return (
        <div
            className="relative group select-none"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* RECTANGULAR COMPACT NODE CARD */}
            <div
                className={`
                    relative rounded-xl w-[236px] flex flex-col
                    backdrop-blur-2xl border transition-all duration-300 cursor-pointer overflow-hidden
                    ${borderStyle}
                `}
            >
                {/* HEADER CAP */}
                <div className={`relative px-2.5 pt-2.5 pb-2 border-b flex items-center justify-between gap-2 transition-colors duration-300 ${headerBg}`}>
                    <div className="flex items-center gap-2 min-w-0 z-10">
                        {/* Icon Container */}
                        <div
                            className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 shadow-sm ${isOffline
                                ? 'bg-red-500/15 dark:bg-red-500/20 border-red-300 dark:border-red-500/40 text-red-600 dark:text-red-400'
                                : isPrimaryRole
                                    ? 'bg-amber-500/15 dark:bg-black/30 border-amber-300 dark:border-amber-500/40 text-amber-600 dark:text-amber-300'
                                    : 'bg-cyan-500/15 dark:bg-black/30 border-cyan-300 dark:border-cyan-500/40 text-cyan-600 dark:text-cyan-300'
                                }`}
                        >
                            <DBTypeIcon dbType={dbType} className="w-5 h-5 shrink-0" />
                        </div>


                        <div className="min-w-0 flex flex-col">
                            {/* Host / IP */}
                            <div className="text-[11px] text-gray-700 dark:text-gray-200 font-mono tracking-tight truncate leading-tight">
                                Host Name: {hostname}
                            </div>

                            <div className='flex gap-1'>
                                <div className="text-[11.5px] font-bold text-slate-900 dark:text-slate-100 font-mono tracking-tight truncate leading-tight" title={host || id}>
                                    {host || id}:{port || '5432'}
                                </div>
                            </div>
                            {/* Role Badge */}
                            <div className="flex items-center gap-1 mt-0.5">

                                <span
                                    className={`px-1 py-0.2 rounded text-[8px] font-extrabold uppercase tracking-wider border shadow-sm ${isOffline
                                        ? 'bg-red-100 dark:bg-red-500/25 text-red-700 dark:text-red-200 border-red-300 dark:border-red-500/45'
                                        : isWasPrimary
                                            ? 'bg-rose-100 dark:bg-rose-500/25 text-rose-700 dark:text-rose-200 border-rose-300 dark:border-rose-500/45'
                                            : isPrimaryRole
                                                ? 'bg-amber-100 dark:bg-amber-500/25 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-500/45'
                                                : 'bg-cyan-100 dark:bg-cyan-500/25 text-cyan-800 dark:text-cyan-200 border-cyan-300 dark:border-cyan-500/45'
                                        }`}
                                >
                                    {activeDisplayRole}
                                </span>

                            </div>
                        </div>
                    </div>

                    {/* Status Pill */}
                    <div className="shrink-0 flex items-center z-10">
                        {isOnline ? (
                            <div className="flex items-center gap-1 bg-emerald-500/15 border border-emerald-500/30 px-1.5 py-0.5 rounded-full" title="Status: Online">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 dark:bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500 dark:bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                                </span>
                                <span className="text-[8px] font-extrabold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">LIVE</span>
                            </div>
                        ) : isDegraded ? (
                            <div className="flex items-center gap-1 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-full" title="Status: Degraded">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 dark:bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.8)]" />
                                <span className="text-[8px] font-extrabold text-amber-700 dark:text-amber-300 uppercase tracking-wider">DEG</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1 bg-red-500/15 dark:bg-red-500/20 border border-red-500/30 dark:border-red-500/40 px-1.5 py-0.5 rounded-full" title="Status: Offline">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.9)]" />
                                <span className="text-[8px] font-extrabold text-red-700 dark:text-red-300 uppercase tracking-wider">OFF</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* STATS BODY */}
                <div className={`p-2 relative z-10 ${isOffline ? 'bg-red-50/50 dark:bg-[#18090b]' : 'bg-white dark:bg-[#0a101f]'}`}>
                    <div className="grid grid-cols-2 gap-1.5">
                        {tiles && tiles.length > 0 ? (
                            tiles.map((tile, idx) => {
                                const style = getTileStyle(tile.key, tile.label, idx, isOffline);
                                const formattedVal = formatTileValue(tile);
                                const IconComponent = style.icon;
                                return (
                                    <div
                                        key={tile.key || idx}
                                        className={`border rounded-lg p-1.5 flex flex-col justify-center relative overflow-hidden shadow-sm ${style.containerClass}`}
                                        title={`${tile.label || tile.key}: ${formattedVal}`}
                                    >
                                        <div className="flex items-center justify-between gap-1 text-[8px] font-extrabold uppercase tracking-tight pl-0.5 min-w-0">
                                            <div className="flex items-center gap-1 min-w-0 truncate">
                                                <IconComponent className={`w-2.5 h-2.5 shrink-0 ${style.iconClass}`} />
                                                <span className="truncate">{tile.label || tile.key}:</span>
                                            </div>
                                            <span className="text-gray-900 dark:text-white font-mono shrink-0 ml-0.5">{formattedVal}</span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <>
                                {/* TPS CARD */}
                                <div className={`border rounded-lg p-1.5 flex flex-col justify-center relative overflow-hidden shadow-sm ${isOffline ? 'bg-red-50 dark:bg-[#220c10] border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300' : 'bg-blue-50 dark:bg-[#0d1c38] border-blue-200 dark:border-[#1d3866] text-blue-700 dark:text-blue-300'
                                    }`}>
                                    <div className="flex items-center gap-1 text-[8px] font-extrabold uppercase tracking-widest pl-0.5 truncate">
                                        <Activity className={`w-2.5 h-2.5 ${isOffline ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`} />
                                        <span>TPS:</span>
                                        <span className='text-gray-900 dark:text-white font-mono'>{displayTps != null ? displayTps.toLocaleString() : '—'}</span>
                                    </div>
                                </div>

                                {/* DB SIZE CARD */}
                                <div className={`border rounded-lg p-1.5 flex flex-col justify-center relative overflow-hidden shadow-sm ${isOffline ? 'bg-red-50 dark:bg-[#220c10] border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300' : 'bg-purple-50 dark:bg-[#1c1333] border-purple-200 dark:border-[#3b2366] text-purple-700 dark:text-purple-300'
                                    }`}>
                                    <div className="flex items-center gap-1 text-[8px] font-extrabold uppercase tracking-widest pl-0.5 truncate">
                                        <HardDrive className={`w-2.5 h-2.5 ${isOffline ? 'text-red-600 dark:text-red-400' : 'text-purple-600 dark:text-purple-400'}`} />
                                        <span>Size:</span>
                                        <span className='text-gray-900 dark:text-white font-mono'>{formatDbSize(dbSizeMb) || '—'}</span>
                                    </div>
                                </div>

                                {/* CONN UTIL CARD */}
                                <div className={`border rounded-lg p-1.5 flex flex-col justify-center relative overflow-hidden shadow-sm ${isOffline ? 'bg-red-50 dark:bg-[#220c10] border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300' : 'bg-cyan-50 dark:bg-[#0a2334] border-cyan-200 dark:border-[#154666] text-cyan-700 dark:text-cyan-300'
                                    }`}>
                                    <div className="flex items-center gap-1 text-[8px] font-extrabold uppercase tracking-widest pl-0.5 truncate">
                                        <Wifi className={`w-2.5 h-2.5 ${isOffline ? 'text-red-600 dark:text-red-400' : 'text-cyan-600 dark:text-cyan-400'}`} />
                                        <span>Conn:</span>
                                        <span className='text-gray-900 dark:text-white font-mono'>{connectionsUtilPct != null ? `${Number(Number(connectionsUtilPct).toFixed(1))}%` : '—'}</span>
                                    </div>
                                </div>

                                {/* CACHE HIT CARD */}
                                <div className={`border rounded-lg p-1.5 flex flex-col justify-center relative overflow-hidden shadow-sm ${isOffline ? 'bg-red-50 dark:bg-[#220c10] border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300' : 'bg-emerald-50 dark:bg-[#092823] border-emerald-200 dark:border-[#135246] text-emerald-700 dark:text-emerald-300'
                                    }`}>
                                    <div className="flex items-center gap-1 text-[8px] font-extrabold uppercase tracking-widest pl-0.5 truncate">
                                        <DatabaseZap className={`w-2.5 h-2.5 ${isOffline ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`} />
                                        <span>Cache:</span>
                                        <span className='text-gray-900 dark:text-white font-mono'>{cacheHitPct != null ? `${cacheHitPct}%` : '—'}</span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* TOP HANDLE */}
            <Handle
                type="target"
                position={Position.Top}
                id="top"
                className={`${handleStyle} ${!isPrimaryRole ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                style={{ top: -4 }}
            />

            {/* BOTTOM HANDLE */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="bottom"
                className={`${handleStyle} ${isPrimaryRole ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                style={{ bottom: -4 }}
            />
        </div>
    );
});

DBNode.displayName = 'DBNode';
export default DBNode;





